/**
 * Shared styling contract for the base UI wrapper components.
 * Keep semantic theme usage here so individual primitives stay thin.
 */

export const gaiaTheme = {
  colors: {
    background: {
      canvas: 'rgb(var(--gaia-color-bg-canvas) / 1)',
      deep: 'rgb(var(--gaia-color-bg-deep) / 1)',
      surface: 'rgb(var(--gaia-color-surface-base) / 1)',
      raised: 'rgb(var(--gaia-color-surface-raised) / 1)',
      alt: 'rgb(var(--gaia-color-surface-alt) / 1)',
    },
    border: {
      subtle: 'rgb(var(--gaia-color-border-subtle) / 1)',
      strong: 'rgb(var(--gaia-color-border-strong) / 1)',
    },
    primary: 'rgb(var(--gaia-color-primary) / 1)',
    primaryHover: 'rgb(var(--gaia-color-primary-hover) / 1)',
    secondary: 'rgb(var(--gaia-color-secondary) / 1)',
    secondaryHover: 'rgb(var(--gaia-color-secondary-hover) / 1)',
    success: 'rgb(var(--gaia-color-success) / 1)',
    successHover: 'rgb(var(--gaia-color-success-hover) / 1)',
    danger: 'rgb(var(--gaia-color-danger) / 1)',
    dangerHover: 'rgb(var(--gaia-color-danger-hover) / 1)',
    warning: 'rgb(var(--gaia-color-warning) / 1)',
    info: 'rgb(var(--gaia-color-info) / 1)',
    focus: 'rgb(var(--gaia-color-focus) / 1)',
    text: {
      primary: 'rgb(var(--gaia-color-text-primary) / 1)',
      secondary: 'rgb(var(--gaia-color-text-secondary) / 1)',
      muted: 'rgb(var(--gaia-color-text-muted) / 1)',
    },
  },
  components: {
    button: {
      // BOU-1036: visual treatment now sourced from design-system chrome.css
      // (.fc-btn + .fc-btn--{primary,secondary,ghost,danger}). Only size
      // utility classes stay here — design-system ships a single button size.
      sizes: {
        xs: 'min-h-8 text-xs',
        sm: 'min-h-9 text-sm',
        md: 'min-h-10 text-sm',
        lg: 'min-h-11 text-base',
      },
    },
    field: {
      container: 'space-y-1.5',
      label: 'block text-sm font-medium text-gaia-text',
      required: 'ml-1 text-gaia-danger',
      helper: 'text-sm text-gaia-text-muted',
      error: 'text-sm text-gaia-danger',
      inputBase: 'w-full rounded-lg border border-gaia-border bg-gaia-surface px-3 py-2 text-gaia-text placeholder-gaia-text-dim transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gaia-focus/30 disabled:opacity-50 disabled:cursor-not-allowed',
      inputState: {
        default: 'hover:border-gaia-border-strong focus:border-gaia-primary',
        error: 'border-gaia-danger focus:border-gaia-danger focus:ring-gaia-danger/20',
      },
    },
    card: {
      base: 'rounded-xl border border-gaia-border bg-gaia-surface text-gaia-text shadow-lg shadow-black/20',
      interactive: 'transition-all duration-150 hover:border-gaia-border-strong hover:bg-gaia-surface-raised cursor-pointer',
      selected: 'border-gaia-primary/60 bg-gaia-surface-raised shadow-xl shadow-black/30',
      header: 'mb-4 border-b border-gaia-border pb-3',
      footer: 'mt-4 border-t border-gaia-border pt-3',
      title: 'text-lg font-semibold text-gaia-text',
      content: 'text-gaia-text-dim',
    },
    select: {
      trigger: 'w-full rounded-lg border border-gaia-border bg-gaia-surface px-4 py-2 text-left text-gaia-text transition-colors duration-150 hover:bg-gaia-surface-raised hover:border-gaia-border-strong focus:outline-none focus:ring-2 focus:ring-gaia-focus/30 focus:border-gaia-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-2 cursor-pointer',
      menu: 'group min-w-[var(--anchor-width)] max-w-[var(--available-width)] overflow-y-auto rounded-xl border border-gaia-border-strong bg-gaia-surface-raised shadow-2xl shadow-black/50 animate-in fade-in',
      item: 'flex cursor-pointer flex-col px-4 py-2.5 text-gaia-text-dim transition-colors hover:bg-gaia-surface-alt hover:text-gaia-text ui-selected:bg-gaia-primary/15 ui-selected:text-gaia-text',
      optionDescription: 'text-xs text-gaia-text-muted ui-selected:text-gaia-text-muted',
      scrollIndicator: 'pointer-events-none absolute left-0 right-0 z-10 flex h-7 items-center justify-center text-gaia-text-muted',
      scrollIndicatorTop: 'top-0 bg-gradient-to-b from-gaia-surface-raised via-gaia-surface-raised to-transparent',
      scrollIndicatorBottom: 'bottom-0 bg-gradient-to-t from-gaia-surface-raised via-gaia-surface-raised to-transparent',
      scrollMore: 'flex items-center justify-center gap-1.5 border-t border-gaia-border px-3 py-1.5 text-xs text-gaia-text-muted bg-gaia-surface-raised',
    },
    alert: {
      base: 'flex items-start gap-3 rounded-xl border p-4 shadow-lg shadow-black/20',
      variants: {
        info: 'border-gaia-info/35 bg-gaia-info/10 text-gaia-info',
        success: 'border-gaia-success/35 bg-gaia-success/10 text-gaia-success',
        warning: 'border-gaia-warning/35 bg-gaia-warning/10 text-gaia-warning',
        error: 'border-gaia-danger/35 bg-gaia-danger/10 text-gaia-danger',
      },
    },
    dropdown: {
      menu: 'rounded-xl border border-gaia-border-strong bg-gaia-surface-raised shadow-2xl shadow-black/50',
      item: 'w-full px-4 py-2 text-left transition-colors',
    },
  },
  animations: {
    fadeIn: 'animate-fadeIn',
    slideUp: 'animate-slideUp',
    slideDown: 'animate-slideDown',
    scaleIn: 'animate-scaleIn',
  },
  a11y: {
    focusRing: 'focus:outline-none focus:ring-2 focus:ring-gaia-focus/30 focus:ring-offset-2 focus:ring-offset-gaia-bg',
    srOnly: 'sr-only',
    notSrOnly: 'not-sr-only',
  },
};

export const cn = (...classes) => classes.filter(Boolean).join(' ');

const buttonSizeAliases = {
  xs: 'xs',
  sm: 'sm',
  small: 'sm',
  md: 'md',
  medium: 'md',
  lg: 'lg',
  large: 'lg',
};

export const normalizeButtonVariant = (variant = 'primary') => {
  return variant || 'primary';
};

export const normalizeButtonSize = (size = 'medium') => {
  return buttonSizeAliases[size] || 'md';
};

const FC_BTN_VARIANTS = new Set(['primary', 'secondary', 'ghost', 'danger']);

export const getButtonClasses = (variant = 'primary', size = 'medium', disabled = false) => {
  const normalizedVariant = normalizeButtonVariant(variant);
  const normalizedSize = normalizeButtonSize(size);
  // BOU-1036: 'error' callsites alias to 'danger' (matches ButtonVariants export);
  // unknown variants fall back to primary so a typo can't render an unstyled button.
  const aliased = normalizedVariant === 'error' ? 'danger' : normalizedVariant;
  const variantClass = FC_BTN_VARIANTS.has(aliased) ? `fc-btn--${aliased}` : 'fc-btn--primary';

  return cn(
    'fc-btn',
    variantClass,
    gaiaTheme.components.button.sizes[normalizedSize],
    gaiaTheme.a11y.focusRing,
    disabled && 'opacity-50'
  );
};

export const getFieldLabelClasses = () => gaiaTheme.components.field.label;

export const getFieldRequiredClasses = () => gaiaTheme.components.field.required;

export const getFieldHelperTextClasses = (hasError = false) => {
  return hasError ? gaiaTheme.components.field.error : gaiaTheme.components.field.helper;
};

export const getInputClasses = (error = false, disabled = false, extraClassName = '') => {
  return cn(
    gaiaTheme.components.field.inputBase,
    error ? gaiaTheme.components.field.inputState.error : gaiaTheme.components.field.inputState.default,
    disabled && 'opacity-50 cursor-not-allowed',
    extraClassName
  );
};

export const getCardClasses = ({ hover = false, selected = false, padding = 'p-4' } = {}) => {
  return cn(
    gaiaTheme.components.card.base,
    padding,
    hover && gaiaTheme.components.card.interactive,
    selected && gaiaTheme.components.card.selected
  );
};

export const getAlertClasses = (variant = 'info') => {
  return cn(
    gaiaTheme.components.alert.base,
    gaiaTheme.components.alert.variants[variant] || gaiaTheme.components.alert.variants.info
  );
};

export default gaiaTheme;
